import React, { useState, useEffect } from 'react';
import type { TunableParam } from '../../../types/TunableParam';
import { getOptions, type OptionItem, coerceForSpec } from '../../../hooks/useDataTypes';

interface DynamicControlProps {
    param: TunableParam;
    currentValue: number | boolean | string;
    onChange: (nodeId: string, paramKey: string, newValue: number | boolean | string) => void;
}

export function DynamicControl({ param, currentValue, onChange }: DynamicControlProps) {
    const spec = param.spec;

    if (!spec) {
        return null;
    }

    if (spec.control === 'dropdown') {
        return (
            <DropdownControl
                param={param}
                currentValue={currentValue}
                onChange={onChange}
            />
        );
    }

    if (spec.control === 'number') {
        return (
            <NumberControl
                param={param}
                currentValue={currentValue as number}
                onChange={onChange}
            />
        );
    }

    if (spec.control === 'checkbox') {
        return (
            <CheckboxControl
                param={param}
                currentValue={currentValue as boolean}
                onChange={onChange}
            />
        );
    }

    return null;
}

function DropdownControl({
    param,
    currentValue,
    onChange,
}: {
    param: TunableParam;
    currentValue: string | number | boolean;
    onChange: (nodeId: string, paramKey: string, newValue: string | number) => void;
}) {
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const spec = param.spec!;

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (spec.options_source) {
                setLoading(true);
                try {
                    const opts = await getOptions(spec.options_source);
                    if (mounted) setOptions(opts);
                } catch (err) {
                    console.error(`Failed to load options for ${spec.options_source}`, err);
                } finally {
                    if (mounted) setLoading(false);
                }
            } else if (spec.options) {
                if (mounted) setOptions(spec.options);
            }
        })();
        return () => { mounted = false; };
    }, [spec.options_source, spec.options]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = coerceForSpec(spec, e.target.value);
        onChange(param.nodeId, param.paramKey, val as string | number);
    };

    return (
        <div>
            <label
                style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: 'var(--text-primary)',
                }}
            >
                {spec.label}
            </label>
            <select
                value={String(currentValue)}
                onChange={handleChange}
                disabled={loading}
                style={{
                    width: '100%',
                    padding: '0.4rem',
                    background: 'var(--bg-app)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--surface-glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    outline: 'none',
                }}
            >
                {loading && <option value="">Loading...</option>}
                {!loading && options.map((opt) => (
                    <option key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

function NumberControl({
    param,
    currentValue,
    onChange,
}: {
    param: TunableParam;
    currentValue: number;
    onChange: (nodeId: string, paramKey: string, newValue: number) => void;
}) {
    const spec = param.spec!;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseFloat(e.target.value);
        onChange(param.nodeId, param.paramKey, val);
    };

    return (
        <div>
            <label
                style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: 'var(--text-primary)',
                }}
            >
                {spec.label}: {currentValue}
            </label>
            <input
                type="number"
                value={currentValue}
                onChange={handleChange}
                style={{
                    width: '100%',
                    padding: '0.4rem',
                    background: 'var(--bg-app)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--surface-glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    outline: 'none',
                }}
            />
        </div>
    );
}

function CheckboxControl({
    param,
    currentValue,
    onChange,
}: {
    param: TunableParam;
    currentValue: boolean;
    onChange: (nodeId: string, paramKey: string, newValue: boolean) => void;
}) {
    const spec = param.spec!;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(param.nodeId, param.paramKey, e.target.checked);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
                type="checkbox"
                checked={currentValue}
                onChange={handleChange}
                style={{
                    width: '16px',
                    height: '16px',
                    accentColor: 'var(--accent-success)',
                }}
            />
            <label
                style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    color: 'var(--text-primary)',
                }}
            >
                {spec.label}
            </label>
        </div>
    );
}

